/**
 * Cycle Landing Page
 * 
 * Landing screen shown when a new cycle is assigned to an operator.
 * Displays cycle details, summary stats, and campaigns before proceeding to Route Overview.
 */

import { useEffect } from 'react';
import { useParams, useLocation } from 'wouter';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useCycleSummary } from '@/hooks/useCycleSummary';
import { MobileScaffold } from '@/components/mobile/MobileScaffold';
import { CycleLandingCard } from '@/components/mobile/CycleLandingCard';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

function CycleLandingContent() {
  const [, setLocation] = useLocation();
  const params = useParams<{ cycleId: string }>();
  const cycleId = params?.cycleId ? parseInt(params.cycleId) : undefined;
  const { user } = useAuthUser();

  const operatorId = user?.id ?? (user as any)?.userId ?? (user as any)?.localId;
  
  // Validate cycleId
  const isValidCycleId = cycleId !== undefined && !isNaN(cycleId);
  
  const {
    data: cycleSummary,
    isLoading,
    error,
  } = useCycleSummary(operatorId, isValidCycleId ? cycleId : undefined);

  // Mark cycle as started when page is viewed
  useEffect(() => {
    if (cycleId) {
      localStorage.setItem(`cycle_${cycleId}_started`, 'true');
    }
  }, [cycleId]);

  const handleStartRoute = () => {
    if (cycleId) {
      setLocation(`/route-overview/${cycleId}`);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <MobileScaffold title="Cycle Summary">
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </MobileScaffold>
    );
  }

  // Error state
  if (error || !cycleSummary || !isValidCycleId) {
    return (
      <MobileScaffold title="Cycle Summary">
        <div className="p-4">
          <Card className="rounded-xl shadow-sm">
            <CardContent className="p-6">
              <p className="text-gray-600">
                {!isValidCycleId 
                  ? 'Invalid cycle ID' 
                  : error instanceof Error 
                    ? error.message 
                    : 'Failed to load cycle summary'}
              </p>
            </CardContent>
          </Card>
        </div>
      </MobileScaffold>
    );
  }

  return (
    <MobileScaffold title="Cycle Summary">
      <CycleLandingCard
        mode="start"
        cycleName={cycleSummary.cycleName}
        period={cycleSummary.period}
        retailerName={cycleSummary.retailerName}
        totalStores={cycleSummary.totalStores}
        totalCampaigns={cycleSummary.totalCampaigns}
        totalTasks={cycleSummary.totalTasks}
        campaigns={cycleSummary.campaigns}
        onAction={handleStartRoute}
      />
    </MobileScaffold>
  );
}

export default function CycleLanding() {
  return <CycleLandingContent />;
}
